/**
 * Pacotes por tipo de negócio da home ("Um pacote pronto para cada tipo de negócio").
 *
 * `apps` são slugs do catálogo público. O preço e a descrição vêm do catálogo no
 * momento do build, então se um app mudar de preço ou sair do ar o pacote acompanha.
 */
export interface Solution {
  id: string;
  label: string;
  title: string;
  desc: string;
  bullets: string[];
  apps: string[];
  /** Nota curta ao lado de um app, ex.: o que vem incluso. */
  notes?: Record<string, string>;
  /** Print em `public/screens/<img>.webp` (existe também .avif e .png). */
  img: string;
  imgAlt: string;
  /** Cliente real (slug de `clients.ts`) que usa esse pacote. */
  clientSlug?: string;
  /** Página de setor (`/setores/:slug`) que aprofunda este pacote. */
  sectorSlug?: string;
}

export const solutions: Solution[] = [
  {
    id: 'hub-comercial',
    label: 'Hub Comercial',
    title: 'Hub Comercial: os pedidos dos marketplaces num pedido só.',
    desc: 'Conecte suas contas de marketplace e receba os pedidos numa lista única. O estoque da Operax é publicado em cada canal, e uma venda em qualquer lugar atualiza o saldo dos outros. Os canais entram conforme a liberação de cada marketplace.',
    bullets: [
      'Pedidos de todos os canais numa fila única',
      'Estoque sincronizado: sem venda de item esgotado',
      'Pedidos e estoque no mesmo login do atendimento',
      'Atendimento pós-venda no WhatsApp oficial',
    ],
    apps: ['commercial-hub', 'inventory-catalog', 'fiscal-nfse', 'whatsapp-catalog', 'pos'],
    sectorSlug: 'varejo-e-comercio',
    img: 'hub-comercial',
    imgAlt: 'Lista de pedidos do Hub Comercial com Mercado Livre e Magalu, situação de cada pedido e produtos para separar',
  },
  {
    id: 'clinicas',
    label: 'Clínicas',
    title: 'Clínicas: agenda, confirma, atende e fatura pelo WhatsApp.',
    desc: 'O CoreMed junta recepção, fila, prontuário eletrônico, receitas, guias de convênio e financeiro do atendimento. O sistema passa a falar paciente, consulta e procedimento.',
    bullets: [
      'Horário marcado na conversa cai na agenda do profissional',
      'Confirmação e lembrete automáticos: menos falta',
      'Senha, chamada no guichê e painel na TV',
      'Pix na consulta e NFS-e com PDF para o paciente',
    ],
    apps: ['clinica', 'fiscal-nfse', 'queue-panels', 'reminders', 'payments', 'customer-success', 'semantic-clinic'],
    sectorSlug: 'clinicas',
    notes: { clinica: 'inclui Agenda e Financeiro' },
    img: 'coremed-recepcao',
    imgAlt: 'Recepção do CoreMed com a agenda do dia, check-in feito e situação de cada paciente',
  },
  {
    id: 'medicos',
    label: 'Médicos particulares',
    title: 'Consultório particular: a agenda enche pelo WhatsApp e o paciente não esquece.',
    desc: 'Para o médico que atende por conta própria, sem a estrutura de uma clínica. O paciente marca na conversa, recebe lembrete, paga por Pix e leva a nota.',
    bullets: [
      'Agenda online com confirmação automática',
      'Lembrete antes da consulta: menos falta',
      'Pix cobrado na conversa, sem maquininha',
      'NFS-e com PDF enviado ao paciente',
    ],
    apps: ['agenda', 'payments', 'reminders', 'fiscal-nfse', 'client-portal', 'semantic-clinic'],
    sectorSlug: 'clinicas',
    img: 'agenda-detalhe',
    imgAlt: 'Agenda da semana com um agendamento aberto: ir para a conversa, reagendar, dar entrada e criar lembrete',
    clientSlug: 'consultorio-dr-lucas',
  },
  {
    id: 'oficinas',
    label: 'Oficinas',
    title: 'Oficina mecânica: do orçamento no WhatsApp à OS fechada.',
    desc: 'Cada veículo fica ligado ao cliente com o histórico de serviços. A ordem de serviço sai do atendimento, com checklist e evidências, e termina com nota e Pix.',
    bullets: [
      'Histórico de serviços por placa e cliente',
      'OS com checklist, fotos e prazo',
      'Orçamento e atendimento do cliente no WhatsApp',
      'Lembrete e pesquisa de satisfação depois do serviço',
    ],
    apps: ['workshop', 'semantic-workshop', 'service-orders', 'inventory-catalog', 'pos', 'payments', 'fiscal-nfse'],
    sectorSlug: 'oficinas-e-autopecas',
    img: 'os-quadro',
    imgAlt: 'Quadro de ordens de serviço por etapa: aprovação, agendada, em execução',
    clientSlug: 'world-car-new',
  },
  {
    id: 'atendimento-ia',
    label: 'Atendimento + IA',
    title: 'Atendimento + IA: a equipe inteira no mesmo número, com IA respondendo.',
    desc: 'Inbox com filas, distribuição e histórico por contato. A IA entende o cliente, responde no WhatsApp e busca na sua base de conhecimento, com você no controle do que ela pode fazer.',
    bullets: [
      'Funil de vendas colado em cada conversa',
      'Chat do site no mesmo inbox',
      'NPS, CSAT e CES enviados na conversa',
      'API para integrar seus sistemas',
    ],
    apps: ['ia', 'customer-success', 'api-operax', 'web-chat-widget', 'reminders'],
    img: 'inbox',
    imgAlt: 'Inbox de atendimento com fila de conversas, mensagens do cliente e dados do contato ao lado',
  },
  {
    id: 'varejo',
    label: 'Varejo',
    title: 'Varejo: balcão, WhatsApp e marketplace com um estoque só.',
    desc: 'PDV no navegador com leitor de código de barras e venda por peso. Com o app Estoque, a venda de produto controlado baixa o saldo sozinha, e o catálogo do WhatsApp traz o carrinho direto para a conversa.',
    bullets: [
      'PDV sem instalar programa',
      'Saldo por local, lote, validade e número de série',
      'NF-e e Pix a partir do atendimento',
      'Importe clientes, produtos e fiado do SISGER',
    ],
    apps: ['pos', 'inventory-catalog', 'whatsapp-catalog', 'fiscal-nfse', 'payments', 'finance', 'migration'],
    sectorSlug: 'varejo-e-comercio',
    img: 'estoque',
    imgAlt: 'Estoque com saldo físico, itens abaixo do mínimo, lotes a vencer e últimos movimentos',
    clientSlug: 'agrodias',
  },
  {
    id: 'restaurantes',
    label: 'Restaurantes',
    title: 'Restaurante e delivery: cardápio com link próprio e pedido na cozinha.',
    desc: 'O cliente monta o pedido no cardápio digital, paga por Pix copia e cola e o pedido aparece no KDS e na comanda impressa. Quem vende também em marketplace pode trazer esses pedidos para o mesmo lugar pelo Hub Comercial.',
    bullets: [
      'Cardápio com foto, categoria e carrinho',
      'Pix copia e cola no pedido, sem taxa de app de entrega',
      'Comanda impressa e tela da cozinha (KDS)',
      'Catálogo do WhatsApp e senha de espera no balcão',
    ],
    apps: ['cardapio', 'whatsapp-catalog', 'queue-panels', 'payments', 'pos', 'linktree'],
    sectorSlug: 'restaurantes-e-delivery',
    img: 'cardapio',
    imgAlt: 'Cardápio digital com vendas do dia, ticket médio, tempo de preparo e status dos pedidos',
  },
  {
    id: 'associacoes',
    label: 'Associações',
    title: 'Associações e proteção veicular: cobrança que chega e associado que se resolve sozinho.',
    desc: 'Cobrança mensal pelo WhatsApp oficial com modelos aprovados pela Meta, e um portal para o associado ver documentos e aprovações.',
    bullets: [
      'Disparo em massa com modelos aprovados pela Meta',
      'Portal do associado com documentos e aprovações',
      'Contrato com assinatura eletrônica por link',
      'Lembrete de vencimento para a equipe e o associado',
    ],
    apps: ['payments', 'finance', 'reminders', 'client-portal', 'contracts'],
    img: 'recebiveis',
    imgAlt: 'Contas a receber com vencimentos, valores restantes e situação de cada cobrança',
    clientSlug: 'protvaco',
  },
  {
    id: 'cursos-eventos',
    label: 'Cursos e eventos',
    title: 'Cursos, mentorias e eventos: inscrição e pagamento dentro da conversa.',
    desc: 'Quem vende conhecimento capta pelo Instagram, inscreve pelo WhatsApp e cobra por Pix. O app Lembretes avisa a equipe e o cliente, e a nota fiscal sai do mesmo atendimento.',
    bullets: [
      'Página de links que vira lead no CRM',
      'Inscrição pelo WhatsApp ou por link',
      'Cobrança por Pix gerada na própria conversa',
      'Lembretes para a equipe e para o cliente',
    ],
    apps: ['events', 'linktree', 'payments', 'reminders', 'fiscal-nfse'],
    sectorSlug: 'eventos-e-cursos',
    img: 'eventos',
    imgAlt: 'Inscrições de um evento com contato, status confirmado ou pendente e data da inscrição',
    clientSlug: 'metodo-stop-gastos',
  },
];
